import { useState, useRef } from 'react';
import { Camera, Upload, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useOrganizationLogo } from '@/hooks/useOrganizationLogo';

interface UploadLogoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UploadLogoDialog = ({ open, onOpenChange }: UploadLogoDialogProps) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { uploadLogo, removeLogo, isUploading, logoUrl } = useOrganizationLogo();

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFileSelect(imageFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      const success = await uploadLogo(selectedFile);
      if (success) {
        onOpenChange(false);
        handleClear();
      }
    }
  };

  const handleRemove = async () => {
    await removeLogo();
    onOpenChange(false);
  };

  const handleClear = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Logo da Organização</DialogTitle>
          <DialogDescription>
            Faça upload de uma logo para sua organização. Formatos aceitos: PNG, JPG, GIF (máx. 5MB)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current Logo Display */}
          {logoUrl && !selectedFile && (
            <div className="text-center space-y-2">
              <div className="text-sm font-medium">Logo atual:</div>
              <div className="flex justify-center">
                <img 
                  src={logoUrl} 
                  alt="Logo atual" 
                  className="h-16 w-16 object-cover rounded-lg border"
                />
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRemove}
                disabled={isUploading}
              >
                <X className="h-4 w-4 mr-2" />
                Remover Logo
              </Button>
            </div>
          )}

          {/* Upload Area */}
          <div
            className={`
              border-2 border-dashed rounded-lg p-6 text-center transition-colors
              ${dragOver ? 'border-primary bg-primary/5' : 'border-gray-300'}
              ${selectedFile ? 'border-green-300 bg-green-50' : ''}
            `}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {selectedFile && previewUrl ? (
              <div className="space-y-3">
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="mx-auto h-20 w-20 object-cover rounded-lg"
                />
                <div className="text-sm text-gray-600">
                  {selectedFile.name}
                </div>
                <div className="flex justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClear}
                    disabled={isUploading}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Limpar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleUpload}
                    disabled={isUploading}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    {isUploading ? 'Enviando...' : 'Confirmar'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Camera className="mx-auto h-8 w-8 text-gray-400" />
                <div className="text-sm text-gray-600">
                  Arraste uma imagem aqui ou clique para selecionar
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Selecionar Arquivo
                </Button>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
